import { io } from "socket.io-client";
import { useEffect, useState, useRef } from "react";
import "../../App.css";

function LiveLogs() {
  const [activeTab, setActiveTab] = useState("peccLogs");
  const [peccMessages, setPeccMessages] = useState([]);
  const [peccErrors, setPeccErrors] = useState([]);
  const [seccData, setSeccData] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  // Load logs from localStorage when page loads
  useEffect(() => {
    setPeccMessages(
      JSON.parse(localStorage.getItem("pecc_logs_messages") || "[]")
    );
    setPeccErrors(
      JSON.parse(localStorage.getItem("pecc_error_messages") || "[]")
    );
    setSeccData(JSON.parse(localStorage.getItem("secc_data_messages") || "[]"));
  }, []);

  // Connect to Socket.IO based on active tab
  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    const baseURL = process.env.REACT_APP_IP_ADDRESS || window.location.origin;
    const namespaceURL = `${baseURL}/${activeTab}`;
    const socket = io(namespaceURL, {
      transports: ["websocket","polling"],
    });

    socketRef.current = socket;

    // Connection events
    socket.on("connect", () => {
      console.log("Connected to namespace:", activeTab);
    });

    // Debug: listen for all events
    socket.onAny((event, data) => {
      console.log("Event:", event, "->", data);
    });

    // Disconnect reason detection
    socket.on("disconnect", (reason) => {
      console.log("Disconnected:", reason);
      switch (reason) {
        case "io server disconnect":
          console.log("Server forced the disconnection");
          break;
        case "io client disconnect":
          console.log("Client disconnected manually");
          break;
        case "ping timeout":
          console.log("Ping timeout (server heartbeat lost)");
          break;
        case "transport close":
          console.log("Transport closed due to browser or network issue");
          break;
        case "transport error":
          console.log("Transport error occurred");
          break;
        case "server namespace disconnect":
          console.log("Server namespace was removed or invalid");
          break;
        default:
          console.log("Unknown disconnect reason");
      }
    });

    // Error events
    socket.on("connect_error", (err) => {
      console.log("Connect Error:", err.message);
    });

    socket.on("reconnect_attempt", (attempt) => {
      console.log("Reconnect Attempt:", attempt);
    });

    socket.on("reconnect", (attempt) => {
      console.log("Reconnected on attempt:", attempt);
    });

    socket.on("reconnect_failed", () => {
      console.log("Reconnect Failed");
    });

    // Namespace-specific listeners
    if (activeTab === "peccLogs") {
      socket.on("mqtt_pecc_messages", (data) => {
        setPeccMessages((prev) => {
          const updated = [...prev, data.message];
          localStorage.setItem("pecc_logs_messages", JSON.stringify(updated));
          return updated;
        });
      });
    }

    if (activeTab === "peccErrors") {
      socket.on("mqtt_pecc_error_messages", (data) => {
        setPeccErrors((prev) => {
          const updated = [...prev, data.message];
          localStorage.setItem("pecc_error_messages", JSON.stringify(updated));
          return updated;
        });
      });
    }

    if (activeTab === "seccData") {
      socket.on("secc_data_messages", (data) => {
        setSeccData((prev) => {
          const updated = [...prev, `${data.topic} --> ${data.payload}`];
          localStorage.setItem("secc_data_messages", JSON.stringify(updated));
          return updated;
        });
      });
    }

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      console.log("Disconnected from namespace:", activeTab);
    };
  }, [activeTab]);

  // Auto-scroll log window
  const activeList =
    activeTab === "peccLogs"
      ? peccMessages
      : activeTab === "peccErrors"
      ? peccErrors
      : seccData;

  useEffect(() => {
    if (autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeList, autoScroll]);

  return (
    <div className="flex h-screen w-full max-w-full overflow-x-hidden overflow-y-auto hide-scrollbar">
      <div className="min-w-0 flex-1 p-4">
        <h2 className="text-2xl font-bold mb-4">MQTT Live Logs</h2>

        <div className="flex flex-wrap gap-2">
          {["peccLogs", "peccErrors", "seccData"].map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 ${
                activeTab === tab ? "bg-blue-500 text-white" : "bg-gray-200"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "peccLogs"
                ? "PECC Logs"
                : tab === "peccErrors"
                ? "PECC Errors"
                : "SECC Data"}
            </button>
          ))}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className="px-3 py-1 bg-gray-600 text-white rounded"
          >
            {autoScroll ? "Disable Auto-Scroll" : "Enable Auto-Scroll"}
          </button>
        </div>

        <div className="w-full max-w-full h-[80vh] bg-black border rounded p-4 overflow-x-auto overflow-y-auto">
          {activeList.length > 0 ? (
            activeList.map((msg, i) => (
              <div
                key={i}
                className={`text-sm mb-1 ${
                  activeTab === "peccErrors" ? "text-red-400" : "text-white"
                }`}
              >
                {msg}
              </div>
            ))
          ) : (
            <div className="text-center text-gray-400 mt-4">
              No messages from {activeTab}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}

export default LiveLogs;
